// Per-page guidance for the Pula guide mascot.
// Each guide: { title, intro, steps:[{ text, target? }] }
// `target` is an optional data-testid used by the guided tour to spotlight an element.
// If the target isn't on the page, the step simply shows as a centred tip (no breakage).

export const GUIDES = {
  "/": {
    title: "Welcome to Pula",
    intro: "Pula is one safe home for connection, fair trade, jobs, learning and more. Here's how to get going.",
    steps: [
      { text: "Tap “Join free” at the top right to create your account — it's free and takes under a minute.", target: "nav-signin" },
      { text: "Use the search bar to find people, goods, jobs, courses or homes in your own words.", target: "global-search-input" },
      { text: "Tap “Ask Halle” (bottom left) to speak or type a question in your language — she points you to the right place.", target: "pula-voice-launcher" },
      { text: "The green bar at the top proves your data is protected — we never sell it, and there are no hidden fees." },
    ],
  },
  "/auth": {
    title: "Create your free account",
    intro: "Joining Pula is quick, private and free. Follow these steps.",
    steps: [
      { text: "Enter your name, email and a password. Your details stay private — we never sell your data." },
      { text: "Tick the box to accept the terms, then tap “Create account”. You're in straight away — no long forms." },
      { text: "Already have an account? Switch to “Sign in” and enter your email and password." },
      { text: "Prefer Google? Tap “Continue with Google” for one-tap sign in." },
    ],
  },
  "/market": {
    title: "Buying & selling, fairly",
    intro: "The Market lets you buy and sell locally with no hidden fees and equal visibility for everyone.",
    steps: [
      { text: "Browse or search for what you need. Tap any item to see details and message the seller." },
      { text: "To sell, tap “Sell” / “Post a listing”, add clear photos, a price and a short description." },
      { text: "Listings are ranked fairly — paying more never pushes you above others. Sponsored items are clearly labelled." },
      { text: "Save items you like to your Wishlist to find them again quickly." },
    ],
  },
  "/local": {
    title: "Find trusted local businesses",
    intro: "Discover verified local businesses and services near you, and book appointments.",
    steps: [
      { text: "Browse by category or town, or search for the service you need (plumber, hair, tutoring…)." },
      { text: "Tap a business to see their profile, reviews and contact details." },
      { text: "If a business takes bookings, tap “Book appointment” to request a time — no payment happens in the app." },
      { text: "Own a business? Tap “Add my business” to get listed for free." },
    ],
  },
  "/jobs": {
    title: "Find work & apply",
    intro: "Browse real local jobs and apply in a few taps — with a respectful, transparent process.",
    steps: [
      { text: "Search or scroll the job list. Tap a job to read the full details." },
      { text: "Tap “Apply” and add a short message. Employers can then invite you to interview." },
      { text: "Track everything under “My applications” — you'll see your status and any interview invites.", target: "my-applications-link" },
      { text: "If you're not selected, you may receive a kind, honest reason to help you next time." },
    ],
  },
  "/learn": {
    title: "Learn new skills — free",
    intro: "Free courses to grow your skills, at your own pace, using very little data.",
    steps: [
      { text: "Browse courses by category or search for a topic you'd like to learn." },
      { text: "Tap a course to start. Your progress is saved so you can continue anytime." },
      { text: "Finish a course to earn a certificate you can share with employers." },
      { text: "Check “My learning” to pick up where you left off." },
    ],
  },
  "/love": {
    title: "Safe, kind connection",
    intro: "Meet people with privacy and dignity first. Your photos stay hidden until you approve someone.",
    steps: [
      { text: "Create your profile — add a few photos, a short bio and what you're looking for." },
      { text: "Browse suggestions or join a group. Send a request to see a full profile." },
      { text: "Your safety comes first: hide your age, block or report anytime, and share your live location with a trusted contact." },
      { text: "Need a private space to reflect on a date? Try the Dating Confidante." },
    ],
  },
  "/confidante": {
    title: "Your private Dating Confidante",
    intro: "A completely private, encrypted space to reflect on your dates and stay safe. Only you can see it.",
    steps: [
      { text: "Add a date by name or nickname, then tell Pula how it went — it listens and gently spots red or green flags." },
      { text: "Everything you write is private and encrypted. It is never shared and never appears anywhere else." },
      { text: "Set a “Safety check-in timer” before a date. If you don't tap “I'm safe” in time, we alert your trusted friend by email.", target: "timer-setup" },
      { text: "If something feels wrong, tap “Safety help” for discreet support and options — you're never alone." },
    ],
  },
  "/safety": {
    title: "Your safety, always first",
    intro: "Pula is built to keep you safe. Here's what's here for you.",
    steps: [
      { text: "Learn how to stay private: hidden photos, blocking, reporting and trusted-contact location sharing." },
      { text: "Use the “Safety Help” tab (right side of the screen) anytime for discreet support." },
      { text: "In immediate danger, always call 999 (or your local emergency number) first." },
    ],
  },
  "/live": {
    title: "Pula Live",
    intro: "Watch and join live community broadcasts — talks, prayer, learning and more.",
    steps: [
      { text: "Tap any live or upcoming broadcast to join and watch." },
      { text: "React, ask questions and take part respectfully — hosts keep it safe for everyone." },
      { text: "Want to host? Start your own broadcast from the Live studio." },
    ],
  },
  "/students": {
    title: "Student Hub",
    intro: "Everything students need — marketplace, gigs, group buys, move-out help and assignment feedback.",
    steps: [
      { text: "Browse the student marketplace and gigs, or start a group buy to save together." },
      { text: "Need feedback on your work? Use “Assignment Review” for proofreading and learning support (never essay-writing)." },
      { text: "Share your voice, find extras, and get sorted for move-out — all in one place." },
    ],
  },
  "/appointments": {
    title: "Your appointments",
    intro: "Request and manage bookings with local businesses — simply and safely.",
    steps: [
      { text: "See your requested and confirmed bookings here." },
      { text: "A provider may accept, decline or suggest a different time — you'll confirm the final time." },
      { text: "No payment happens in the app — you pay the provider directly, in person." },
    ],
  },
};

const DEFAULT_GUIDE = {
  title: "How this page works",
  intro: "Here are a few tips to help you get the most from Pula.",
  steps: [
    { text: "Not signed in yet? Tap “Join free” at the top to create your free account in under a minute." },
    { text: "Use the search bar to quickly find people, goods, jobs, courses or homes." },
    { text: "Tap “Ask Halle” (bottom left) to ask anything in your own language and get pointed the right way." },
    { text: "Your data is always protected — we never sell it, and there are no hidden fees." },
  ],
};

// ---- Agent Portal (staff) guides ----
export const PORTAL_GUIDES = {
  "/portal/dashboard": {
    title: "Your portal dashboard",
    intro: "This is your home base as a Pula/Mavula team member. Here's what to look at first.",
    steps: [
      { text: "The cards at the top show your key numbers at a glance — clients, cases and anything needing attention." },
      { text: "Use the left menu to move between Clients, Cases, Resources and more." },
      { text: "Anything urgent (like a safety concern) is flagged so you never miss it." },
      { text: "Remember: we never handle money and never ask about immigration status abroad." },
    ],
  },
  "/portal/clients": {
    title: "Managing clients",
    intro: "Add and support the people you care for, with their privacy protected at every step.",
    steps: [
      { text: "Tap “Add client” to register someone new — only collect what you genuinely need." },
      { text: "Open any client to see their details, notes and linked cases." },
      { text: "Keep records accurate and kind — clients come first, always." },
      { text: "All client data is private and secure; only access what your role requires." },
    ],
  },
  "/portal/cases": {
    title: "Working with cases",
    intro: "Track the support you're providing from start to finish.",
    steps: [
      { text: "Create a case to record a piece of work or support for a client." },
      { text: "Update the status as things progress so the whole team stays informed." },
      { text: "Add clear notes — they help colleagues pick up where you left off." },
      { text: "Everything is logged in a tamper-evident audit trail for accountability." },
    ],
  },
  "/portal/safety-concerns": {
    title: "Safety Concerns inbox",
    intro: "Handle safety flags with care. Only minimal information is shown — private chats are never exposed.",
    steps: [
      { text: "Review each concern in the list — you'll see only what's needed to help, never private messages." },
      { text: "Reach out or escalate using your safeguarding process." },
      { text: "Mark a concern “Resolved” when handled, or reopen it if it needs more attention." },
      { text: "In immediate danger, emergency services (999) always come first." },
    ],
  },
  "/portal/resources": {
    title: "Resources library",
    intro: "Guides, templates and helpful materials for your work.",
    steps: [
      { text: "Browse or search for the resource you need." },
      { text: "Open a resource to read or share it with a client or colleague." },
      { text: "Check back often — the library grows as the team adds more." },
    ],
  },
  "/portal/onboarding": {
    title: "Getting started",
    intro: "New to the portal? This short guide gets you confident fast.",
    steps: [
      { text: "Follow the steps here to set up your profile and learn the basics." },
      { text: "Explore Clients and Cases — that's where most of your day-to-day happens." },
      { text: "Need help anytime? Use “Get help (WhatsApp)” at the bottom of the menu." },
    ],
  },
  "/portal/exec": {
    title: "Executive overview",
    intro: "A high-level view of how the whole operation is performing.",
    steps: [
      { text: "See live metrics across the team, clients and cases." },
      { text: "Use Team and Analytics from the menu to go deeper." },
      { text: "The AI briefing turns these numbers into plain-language insight (advisory only)." },
    ],
  },
};

const PORTAL_DEFAULT = {
  title: "Agent Portal",
  intro: "Welcome to the Pula & Mavula staff portal. Here's how to find your way around.",
  steps: [
    { text: "Use the left menu to move between Dashboard, Clients, Cases and more." },
    { text: "Your role decides what you can see and do — you'll only see what's relevant to you." },
    { text: "Need help? Use “Get help (WhatsApp)” at the bottom of the menu." },
    { text: "We never handle money and never ask about immigration status abroad — clients first, always." },
  ],
};

// Ordered playlist for the one-tap "Demo tour" (public platform walkthrough).
export const DEMO_ROUTES = [
  { path: "/", label: "Welcome & Home" },
  { path: "/market", label: "Marketplace" },
  { path: "/local", label: "Local Businesses" },
  { path: "/jobs", label: "Jobs" },
  { path: "/learn", label: "Free Learning" },
  { path: "/love", label: "Safe Connection" },
  { path: "/confidante", label: "Dating Confidante" },
  { path: "/appointments", label: "Appointments" },
  { path: "/students", label: "Student Hub" },
];

// Ordered playlist for the staff "training tour" (Agent Portal walkthrough).
export const PORTAL_DEMO_ROUTES = [
  { path: "/portal/dashboard", label: "Dashboard" },
  { path: "/portal/clients", label: "Clients" },
  { path: "/portal/cases", label: "Cases" },
  { path: "/portal/safety-concerns", label: "Safety Concerns" },
  { path: "/portal/resources", label: "Resources" },
];

export function getGuide(pathname) {
  if (pathname.startsWith("/portal")) {
    if (PORTAL_GUIDES[pathname]) return { key: pathname, ...PORTAL_GUIDES[pathname] };
    const pm = Object.keys(PORTAL_GUIDES)
      .filter((p) => pathname.startsWith(p))
      .sort((a, b) => b.length - a.length)[0];
    if (pm) return { key: pm, ...PORTAL_GUIDES[pm] };
    return { key: "_portal_default", ...PORTAL_DEFAULT };
  }
  if (GUIDES[pathname]) return { key: pathname, ...GUIDES[pathname] };
  // longest matching prefix (e.g. /market/123 -> /market)
  const match = Object.keys(GUIDES)
    .filter((p) => p !== "/" && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  if (match) return { key: match, ...GUIDES[match] };
  return { key: "_default", ...DEFAULT_GUIDE };
}
